import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { CaGroupBy, REPARTITION_CA_DATA, RepartitionCaDetail } from "../../models/data.model";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { SharedModule } from "../../../../shared/shared.module";
import { Chart } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { LocalStorageService } from "../../../../shared/services/local-storage.service";
import { ActivityInfosService } from "../../../activity_infos/services/activity_infos.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { RepartitionCaDialogComponent } from "./repartition-ca-dialog.component";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { MatMenuModule } from "@angular/material/menu";
import { MatIcon } from "@angular/material/icon";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";

const STORAGE_KEY = "justup-widget-repartition-ca";

@Component({
  selector: "vl-widget-repartition-par-client",
  standalone: true,
  imports: [SharedModule, WidgetTitleComponent, ChipsToggleComponent, ChipsComponent, MatMenuModule, MatIcon, MatSlideToggleModule],
  host: { class: "widget-component" },
  templateUrl: "./widget-repartition-par-client.component.html",
  styleUrl: "./widget-repartition-par-client.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetRepartitionParClientComponent implements OnDestroy {
  data = input<REPARTITION_CA_DATA>();
  widget = input<Widget>();
  metadata = input<EntityMetadata>();
  /** Sous-ensemble de group-by à afficher (ex: ['byArticle','byFamille'] pour stats tiers). */
  availableGroupBy = input<CaGroupBy[] | null>(null);
  /** Cache le menu période (utile si la période est pilotée en amont). */
  hidePeriod = input<boolean>(false);
  /** Override du titre. Par défaut : "dashboard_ui.repartition_ca". */
  customTitle = input<string | null>(null);
  /** Clé unique pour la persistance des filtres (évite les collisions entre usages). */
  storageKey = input<string>(STORAGE_KEY);
  labels = signal(null);
  canvasRef = viewChild<ElementRef>("repart");
  chart: any;
  maxItems = 8;

  private dialogsService = inject(DialogsService);
  private localStorage = inject(LocalStorageService);
  private activityInfos = toSignal(inject(ActivityInfosService).currentActivityInfos$);

  // ── Persistent filters ──
  groupBy = signal<CaGroupBy>("byClient");
  period = signal<string>("Exercice");
  /** Inclure la main-d'œuvre dans le calcul de la marge. Par défaut `true` —
   *  l'utilisateur peut exclure la MO pour raisonner en marge "composants purs". */
  includeMO = signal<boolean>(true);
  /** Inclure la remise pied de facture dans le CA affiché. Par défaut `true` —
   *  l'utilisateur peut basculer en CA "brut" (avant remise pied) via le toggle. */
  includeRemisePied = signal<boolean>(true);

  private readonly ALL_GROUP_BY: { name: string; value: CaGroupBy }[] = [
    { name: "Client", value: "byClient" },
    { name: "Article", value: "byArticle" },
    { name: "Famille", value: "byFamille" },
    { name: "Groupe", value: "byGroupeTiers" },
  ];

  /** Group-by filtrés selon `availableGroupBy`. */
  groupByElements = computed(() => {
    const allowed = this.availableGroupBy();
    if (!allowed?.length) return this.ALL_GROUP_BY;
    const allowedSet = new Set(allowed);
    return this.ALL_GROUP_BY.filter((g) => allowedSet.has(g.value));
  });

  periodElements = [
    { name: "1M", value: "1M" },
    { name: "3M", value: "3M" },
    { name: "12M", value: "12M" },
    { name: "Exercice", value: "Exercice" },
    { name: "Ex. passé", value: "Exercice passé" },
    { name: "Tout", value: "ALL" },
  ];

  // ── Compute date range from period ──
  private dateRange = computed<{ start: Date; end: Date }>(() => {
    const p = this.period();
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    let start: Date;

    switch (p) {
      case "1M":
        start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case "3M":
        start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case "12M":
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case "Exercice": {
        const ai = this.activityInfos();
        if (ai?.dateDebutExercice) {
          start = this.ddmmToDate(ai.dateDebutExercice, now.getFullYear());
          if (start > now) start = this.ddmmToDate(ai.dateDebutExercice, now.getFullYear() - 1);
        } else {
          start = new Date(now.getFullYear(), 0, 1);
        }
        break;
      }
      case "Exercice passé": {
        const ai = this.activityInfos();
        if (ai?.dateDebutExercice) {
          const currentStart = this.ddmmToDate(ai.dateDebutExercice, now.getFullYear());
          if (currentStart > now) {
            start = this.ddmmToDate(ai.dateDebutExercice, now.getFullYear() - 2);
            return { start, end: this.ddmmToDate(ai.dateDebutExercice, now.getFullYear() - 1) };
          }
          start = this.ddmmToDate(ai.dateDebutExercice, now.getFullYear() - 1);
          return { start, end: currentStart };
        }
        start = new Date(now.getFullYear() - 1, 0, 1);
        return { start, end: new Date(now.getFullYear(), 0, 1) };
      }
      case "ALL":
      default:
        start = new Date(2020, 0, 1);
        break;
    }

    return { start, end };
  });

  // ── Filter details by date range ──
  private filteredDetails = computed<RepartitionCaDetail[]>(() => {
    const details = this.data()?.details ?? [];
    if (this.hidePeriod()) return details;
    const { start, end } = this.dateRange();
    if (!details.length) return details;
    return details.filter((d) => {
      const dt = new Date(String(d.date));
      return !isNaN(dt.getTime()) && dt >= start && dt < end;
    });
  });

  // ── Recompute aggregations from filtered details ──
  private filteredData = computed<REPARTITION_CA_DATA | null>(() => {
    const raw = this.data();
    if (!raw) return null;
    const details = this.filteredDetails();

    const groupFieldMap: Record<CaGroupBy, keyof RepartitionCaDetail> = {
      byClient: "tiersDenomination", byArticle: "articleDenomination",
      byFamille: "familleDenomination", byGroupeTiers: "tiersGroupe",
    };

    const maps: Record<string, Map<string, number>> = {
      byClient: new Map(), byArticle: new Map(), byFamille: new Map(), byGroupeTiers: new Map(),
    };
    let total = 0;

    for (const d of details) {
      const montant = this.montantOf(d);
      total += montant;
      for (const [key, field] of Object.entries(groupFieldMap)) {
        const name = String(d[field]);
        maps[key].set(name, (maps[key].get(name) ?? 0) + montant);
      }
    }

    const toArray = (m: Map<string, number>) =>
      [...m.entries()].map(([denomination, t]) => ({ denomination, total: t }));

    return {
      byClient: toArray(maps.byClient),
      byArticle: toArray(maps.byArticle),
      byFamille: toArray(maps.byFamille),
      byGroupeTiers: toArray(maps.byGroupeTiers),
      details,
      total,
    };
  });

  activeData = computed(() => {
    const d = this.filteredData();
    if (!d) return null;
    return d[this.groupBy()]?.filter((item) => item.total > 0) ?? null;
  });

  caTotal = computed(() => {
    const d = this.filteredData();
    if (!d) return "- €";
    return WidgetHelperService.formatNumeric(d.total) + " €";
  });

  /**
   * Coût unitaire d'une ligne de CA, conditionné par le toggle `includeMO`.
   * Retourne `null` si la ligne n'est pas marge-traçable (CA comptabilisé
   * mais marge non calculable).
   */
  private coutOf(det: RepartitionCaDetail): number | null {
    if (det.marginTracable === false) return null;
    return (det.coutMateriel ?? 0) + (this.includeMO() ? (det.coutMO ?? 0) : 0);
  }

  /** Montant HT d'une ligne selon le toggle `includeRemisePied`. */
  private montantOf(det: RepartitionCaDetail): number {
    return this.includeRemisePied() ? det.montantHT : (det.montantBrut ?? det.montantHT);
  }

  margeLabel = computed(() => {
    const d = this.filteredData();
    if (!d || !d.details.length) return null;
    // Marge calculée uniquement sur les lignes traçables (coutOf != null).
    let caTracable = 0;
    let coutTracable = 0;
    for (const det of d.details) {
      const cout = this.coutOf(det);
      if (cout == null) continue;
      caTracable += this.montantOf(det);
      coutTracable += cout;
    }
    if (caTracable <= 0) return null;
    const taux = ((caTracable - coutTracable) / caTracable) * 100;
    const suffix = this.includeMO() ? "" : " (hors MO)";
    const couvertPct = d.total > 0 ? (caTracable / d.total) * 100 : 100;
    const couverture =
      couvertPct >= 99.5 ? "" : ` · sur ${couvertPct.toFixed(0)}% du CA`;
    return `Marge ${taux.toFixed(0)}%${suffix}${couverture}`;
  });

  constructor() {
    // Initialisation : restaure les filtres persistés et garantit que `groupBy`
    // reste dans la liste autorisée (ex: passage d'un dashboard vers stats tiers).
    // Les lectures/écritures de localStorage + signals sont en `untracked` pour
    // que l'effect ne se re-déclenche pas à chaque clic utilisateur (ce qui
    // réécraserait immédiatement la sélection par la valeur stockée).
    effect(
      () => {
        const key = this.storageKey();
        const allowed = this.groupByElements().map((g) => g.value);
        untracked(() => {
          const savedGroupBy = (this.localStorage.getWithTTL(`${key}-groupBy`) as CaGroupBy) ?? "byClient";
          const savedPeriod = (this.localStorage.getWithTTL(`${key}-period`) as string) ?? "Exercice";
          const savedIncludeMO = this.localStorage.getWithTTL(`${key}-includeMO`);
          const savedIncludeRemisePied = this.localStorage.getWithTTL(`${key}-includeRemisePied`);
          const normalized = allowed.includes(savedGroupBy) ? savedGroupBy : (allowed[0] ?? "byClient");
          if (this.groupBy() !== normalized) this.groupBy.set(normalized);
          if (this.period() !== savedPeriod) this.period.set(savedPeriod);
          if (typeof savedIncludeMO === "boolean" && this.includeMO() !== savedIncludeMO) {
            this.includeMO.set(savedIncludeMO);
          }
          if (typeof savedIncludeRemisePied === "boolean" && this.includeRemisePied() !== savedIncludeRemisePied) {
            this.includeRemisePied.set(savedIncludeRemisePied);
          }
        });
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const items = this.activeData();
        // Lu explicitement pour que le chart se redessine quand les toggles
        // MO / remise pied changent (les agrégations en dépendent).
        this.includeMO();
        this.includeRemisePied();
        if (items?.length && this.canvasRef()) {
          this.buildChart(items);
        }
      },
      { allowSignalWrites: true },
    );

    // Persist filters
    effect(() => {
      const key = this.storageKey();
      this.localStorage.putWithTTL(`${key}-groupBy`, this.groupBy());
      this.localStorage.putWithTTL(`${key}-period`, this.period());
      this.localStorage.putWithTTL(`${key}-includeMO`, this.includeMO());
      this.localStorage.putWithTTL(`${key}-includeRemisePied`, this.includeRemisePied());
    });
  }

  buildChart(items: { denomination: string; total: number }[]) {
    const sorted = [...items].sort((a, b) => b.total - a.total);
    const top = sorted.slice(0, this.maxItems);
    const rest = sorted.slice(this.maxItems);
    const labels = top.map((d) => d.denomination);
    const caValues = top.map((d) => d.total);

    if (rest.length > 0) {
      labels.push("Autres");
      caValues.push(rest.reduce((s, d) => s + d.total, 0));
    }

    if (!labels.length) return;

    // Compute marge per item from filtered details
    const fd = this.filteredData();
    const details = fd?.details ?? [];
    const gField = this.groupFieldMap[this.groupBy()];
    const margeMap = new Map<string, number>();
    for (const d of details) {
      const key = String(d[gField]);
      margeMap.set(key, (margeMap.get(key) ?? 0) + this.montantOf(d) - this.coutOf(d));
    }

    const margeValues = top.map((d) => Math.max(0, margeMap.get(d.denomination) ?? 0));
    if (rest.length > 0) {
      margeValues.push(Math.max(0, rest.reduce((s, d) => s + (margeMap.get(d.denomination) ?? 0), 0)));
    }

    const canvas = this.canvasRef().nativeElement;
    const ctx = canvas.getContext("2d");
    if (this.chart) this.chart.destroy();

    const palette = [
      "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
      "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
      "#8cd17d", "#a0cbe8",
    ];
    const colors = labels.map((_: string, i: number) => palette[i % palette.length]);
    const colorsAlpha = colors.map((c: string) => c + "80");

    this.chart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          { label: "CA", data: caValues, backgroundColor: colors, borderColor: "#fff", borderWidth: 1 },
          { label: "Marge", data: margeValues, backgroundColor: colorsAlpha, borderColor: "#fff", borderWidth: 1 },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const v = ctx.parsed as number;
                return `${ctx.dataset.label}: ${WidgetHelperService.formatNumeric(v)} €`;
              },
            },
          },
        },
      },
    });

    const total = caValues.reduce((a: number, b: number) => a + b, 0);

    this.labels.set(
      labels.map((label: string, i: number) => ({
        label,
        percentage: ((caValues[i] / total) * 100).toFixed(0),
        color: colors[i],
      })),
    );
  }

  private groupFieldMap: Record<CaGroupBy, keyof RepartitionCaDetail> = {
    byClient: "tiersDenomination", byArticle: "articleDenomination",
    byFamille: "familleDenomination", byGroupeTiers: "tiersGroupe",
  };

  openFullList() {
    const fd = this.filteredData();
    const items = this.activeData();
    if (!items?.length || !fd) return;

    const colorMap: Record<string, string> = {};
    const labels = this.labels();
    if (labels) {
      for (const l of labels) colorMap[l.label] = l.color;
    }

    this.dialogsService.open(
      RepartitionCaDialogComponent,
      "min(95vw, 860px)",
      "fit-content",
      {
        title: this.groupByLabels[this.groupBy()],
        items,
        details: fd.details,
        total: fd.total,
        groupBy: this.groupBy(),
        colorMap,
        fullData: fd,
        includeMO: this.includeMO(),
        includeRemisePied: this.includeRemisePied(),
      },
    );
  }

  private groupByLabels: Record<CaGroupBy, string> = {
    byClient: "Répartition CA — Client",
    byArticle: "Répartition CA — Article",
    byFamille: "Répartition CA — Famille",
    byGroupeTiers: "Répartition CA — Groupe de tiers",
  };

  private ddmmToDate(ddmm: string, year: number): Date {
    const [dd, mm] = ddmm.split("/").map(parseNumber);
    return new Date(year, mm - 1, dd);
  }

  private saveFilter(key: string, value: string) {
    this.localStorage.putWithTTL(`${STORAGE_KEY}-${key}`, value);
  }

  private restoreFilter<T extends string>(key: string, defaultValue: T): T {
    return (this.localStorage.getWithTTL(`${STORAGE_KEY}-${key}`) as T) ?? defaultValue;
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
